import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { t, getCurrentLanguage } from '../../i18n/translations'
import { logger } from '../../utils/logger'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://api.shop.bg502.ru/api/v1'

interface StaticPageData {
  slug: string
  title: string
  content: string
  updated_at: string
}

export default function StaticPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()

  const [page, setPage] = useState<StaticPageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPage = async () => {
      if (!slug) return

      try {
        setLoading(true)
        const lang = getCurrentLanguage()
        const response = await fetch(`${API_BASE_URL}/pages/${slug}?lang=${lang}`)

        if (!response.ok) {
          throw new Error('Page not found')
        }

        const data = await response.json()
        setPage(data)
      } catch (err) {
        logger.error('Failed to fetch static page', err)
        setError('Page not found')
      } finally {
        setLoading(false)
      }
    }

    fetchPage()
  }, [slug])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">{t('common.loading')}</div>
      </div>
    )
  }

  if (error || !page) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
        <p className="text-gray-500 mb-4">{error || 'Page not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="text-blue-500 hover:text-blue-600"
        >
          {t('common.back')}
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="font-medium">{t('common.back')}</span>
          </button>
          <h1 className="text-lg font-semibold text-gray-800 truncate max-w-[200px]">
            {page.title}
          </h1>
          <div className="w-16" />
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">{page.title}</h1>

          <div className="prose prose-gray max-w-none">
            {/* Render content - could be enhanced with markdown parser */}
            {page.content.split('\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 mb-4">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-gray-100 text-sm text-gray-500">
            Last updated: {new Date(page.updated_at).toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Footer with legal links */}
      <div className="p-4">
        <div className="flex justify-center gap-4 text-sm">
          <button
            onClick={() => navigate('/page/privacy-policy')}
            className={`text-gray-500 hover:text-gray-700 ${
              slug === 'privacy-policy' ? 'font-semibold text-blue-600' : ''
            }`}
          >
            {t('legal.privacyPolicy')}
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => navigate('/page/terms-of-service')}
            className={`text-gray-500 hover:text-gray-700 ${
              slug === 'terms-of-service' ? 'font-semibold text-blue-600' : ''
            }`}
          >
            {t('legal.termsOfService')}
          </button>
          <span className="text-gray-300">|</span>
          <button
            onClick={() => navigate('/page/about')}
            className={`text-gray-500 hover:text-gray-700 ${
              slug === 'about' ? 'font-semibold text-blue-600' : ''
            }`}
          >
            {t('legal.about')}
          </button>
        </div>
      </div>
    </div>
  )
}
